import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants/contstants';
import { userService } from '../services/userService';
import styles from './Onboarding.module.css';

type OnboardingStep = 'code' | 'details' | 'complete';

const getApiError = (error: any, fallback: string) => {
  const response = error?.response?.data;
  if (Array.isArray(response?.errors) && response.errors.length > 0) {
    return response.errors.join(' ');
  }
  return response?.message || fallback;
};

const Onboarding = () => {
  const [step, setStep] = useState<OnboardingStep>('code');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [sessionExpiresAt, setSessionExpiresAt] = useState('');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (code.trim().length < 6) {
      setError('Enter the invitation code from your email.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await userService.verifyGraduateInviteCode(code.trim());
      if (!response.isSuccess || !response.data) {
        setError(response.message || 'That invitation code is invalid or expired.');
        return;
      }
      setEmail(response.data.email);
      setSessionToken(response.data.token);
      setSessionExpiresAt(response.data.expiresAtUtc);
      setStep('details');
    } catch (requestError) {
      setError(getApiError(requestError, 'That invitation code is invalid or expired.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const completeRegistration = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (fullName.trim().length < 2) {
      setError('Enter your full name.');
      return;
    }
    if (password.length < 8 || !/[A-Z]/.test(password) || !/\d/.test(password)) {
      setError('Use at least 8 characters, including one uppercase letter and one number.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await userService.completeGraduateOnboarding({
        token: sessionToken,
        fullName: fullName.trim(),
        password,
        ...(dateOfBirth ? { dateOfBirth } : {}),
      });

      if (!response.isSuccess) {
        setError(response.message || 'Registration could not be completed.');
        return;
      }
      setStep('complete');
    } catch (requestError) {
      setError(getApiError(requestError, 'Registration could not be completed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <div className={styles.eyebrow}>247LABS ACADEMY</div>
        {step === 'code' && (
          <>
            <h1 className={styles.title}>Start your screening</h1>
            <p className={styles.subtitle}>
              Enter the one-time code from your email to begin the CCAT screening assessment for the graduate application.
            </p>
            <form onSubmit={verifyCode} className={styles.form}>
              <label className={styles.label} htmlFor="invite-code">Invitation code</label>
              <input
                id="invite-code"
                className={styles.input}
                value={code}
                onChange={event => setCode(event.target.value.toUpperCase())}
                placeholder="e.g. 7KQ4MZ2P"
                autoComplete="one-time-code"
                autoFocus
                maxLength={32}
              />
              {error && <p className={styles.error}>{error}</p>}
              <button className={styles.primaryButton} type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Checking code…' : 'Verify code'}
              </button>
            </form>
          </>
        )}

        {step === 'details' && (
          <>
            <h1 className={styles.title}>Complete your registration</h1>
            <p className={styles.subtitle}>
              Registering <strong>{email}</strong>. This session expires {sessionExpiresAt ? new Date(sessionExpiresAt).toLocaleString() : 'soon'}.
            </p>
            <form onSubmit={completeRegistration} className={styles.form}>
              <label className={styles.label} htmlFor="full-name">Full name</label>
              <input id="full-name" className={styles.input} value={fullName} onChange={event => setFullName(event.target.value)} autoFocus />

              <label className={styles.label} htmlFor="date-of-birth">Date of birth <span>(optional)</span></label>
              <input id="date-of-birth" type="date" className={styles.input} value={dateOfBirth} onChange={event => setDateOfBirth(event.target.value)} />

              <label className={styles.label} htmlFor="password">Create password</label>
              <input id="password" type="password" className={styles.input} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" />

              <label className={styles.label} htmlFor="confirm-password">Confirm password</label>
              <input id="confirm-password" type="password" className={styles.input} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} autoComplete="new-password" />

              {error && <p className={styles.error}>{error}</p>}
              <button className={styles.primaryButton} type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating account…' : 'Complete registration'}
              </button>
              <button className={styles.secondaryButton} type="button" onClick={() => { setStep('code'); setSessionToken(''); setError(''); }}>
                Use a different code
              </button>
            </form>
          </>
        )}

        {step === 'complete' && (
          <div className={styles.successState}>
            <div className={styles.successIcon}>✓</div>
            <h1 className={styles.title}>You’re in</h1>
            <p className={styles.subtitle}>
              Your 247Labs Academy account is ready. Sign in to access the CCAT screening assessment when it is available.
            </p>
            <Link to={ROUTES.LOGIN} className={styles.primaryButton}>Go to sign in</Link>
          </div>
        )}

        {step !== 'complete' && (
          <p className={styles.footerLink}>
            Already have an account? <Link to={ROUTES.LOGIN}>Sign in</Link>
          </p>
        )}
      </section>
    </main>
  );
};

export default Onboarding;
